import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { LeafletProvider } from 'react-leaflet';
import { useMediaQuery, useTheme } from '@material-ui/core';
import Coverage from '../Coverage/Coverage';
import { MapControlsContext, useMapControls } from './MapControlsContext';
import { useStyles } from './MapControls.styles';

export function visibleMapBounds(rect, width, height) {
  const left = Math.max(0, rect.left);
  const top = Math.max(0, rect.top);
  return {
    left, top,
    width: Math.max(0, Math.min(width, rect.right) - left),
    height: Math.max(0, Math.min(height, rect.bottom) - top),
  };
}

export function SharedCoverage() {
  const { leaflet } = useMapControls();
  return <LeafletProvider value={leaflet}><Coverage /></LeafletProvider>;
}

export function MapControlsProvider({ children }) {
  const classes = useStyles();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [leaflet, setLeaflet] = useState({});
  const [bounds, setBounds] = useState(null);
  const [active, setActive] = useState(null);
  const [host, setHost] = useState(null);
  const frame = useRef(null);
  const launchers = useRef({});
  const close = () => {
    const button = launchers.current[active];
    setActive(null);
    if (button) button.focus({ preventScroll: true });
  };
  const map = leaflet.map;
  useEffect(() => {
    if (!map) { setBounds(null); return undefined; }
    const element = map.getContainer();
    const measure = () => setBounds(visibleMapBounds(
      element.getBoundingClientRect(), window.innerWidth, window.innerHeight
    ));
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (observer) observer.observe(element);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      if (observer) observer.disconnect();
    };
  }, [map]);
  useEffect(() => {
    if (!active || !host) return undefined;
    const panel = host.querySelector(`#map-control-${active}`);
    const target = panel && panel.querySelector('button, input, select, [tabindex]');
    if (target) target.focus({ preventScroll: true });
    const keyboard = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setActive(null);
        const button = launchers.current[active];
        if (button) button.focus({ preventScroll: true });
      }
      if (mobile && event.key === 'Tab' && frame.current) {
        const elements = Array.from(frame.current.querySelectorAll(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex="0"]'
        )).filter((element) => !element.closest('[hidden]'));
        const first = elements[0];
        const last = elements[elements.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault(); if (last) last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); if (first) first.focus();
        }
      }
    };
    document.addEventListener('keydown', keyboard);
    return () => document.removeEventListener('keydown', keyboard);
  }, [active, mobile, host]);
  const visible = bounds && bounds.width >= 80 && bounds.height >= 100;
  const position = mobile ? {
    left: 8, right: 8, bottom: 'max(8px, env(safe-area-inset-bottom))',
    '--map-control-width': 'calc(100vw - 16px)',
    '--map-control-height': 'calc(80dvh - 56px)',
  } : {
    left: bounds ? bounds.left + Math.max(8, bounds.width - 448) : 0,
    top: bounds ? bounds.top + bounds.height - 48 : 0,
    width: bounds ? Math.max(0, Math.min(440, bounds.width - 16)) : 0,
    '--map-control-width': bounds ? `${Math.max(0, bounds.width - 16)}px` : '0px',
    '--map-control-height': bounds ? `${Math.max(0, bounds.height - 64)}px` : '0px',
  };
  return (
    <MapControlsContext.Provider value={{ leaflet, setLeaflet, active, setActive, close, host, launchers }}>
      {children}
      {createPortal(
        <div ref={frame} className={classes.frame}
          style={{ ...position, ...(!(mobile ? map : visible) ? { visibility: 'hidden', pointerEvents: 'none' } : {}) }}
          role={mobile && active ? 'dialog' : 'region'}
          aria-label="Map controls">
          <div ref={setHost} style={{ display: 'contents' }} />
        </div>, document.body
      )}
    </MapControlsContext.Provider>
  );
}
MapControlsProvider.propTypes = { children: PropTypes.node.isRequired };

export function MapControl({ id, launcher, children }) {
  const classes = useStyles();
  const controls = useMapControls();
  const setActive = controls && controls.setActive;
  useEffect(() => () => {
    if (setActive) setActive((current) => current === id ? null : current);
  }, [id, setActive]);
  if (!controls || !controls.host) return null;
  return createPortal(<>
    {launcher}
    <div id={`map-control-${id}`} className={classes.panel} hidden={controls.active !== id}>
      {children}
    </div>
  </>, controls.host);
}
MapControl.propTypes = {
  id: PropTypes.string.isRequired, launcher: PropTypes.node.isRequired, children: PropTypes.node.isRequired,
};
